// frontend/src/components/wizard/StepMapping/MappingTopBar.jsx
import React, { useState, useRef } from 'react';
import {
  Wand2, Loader2, Trash2, Lightbulb, ChevronDown, Search, X, Download,
  Upload, BarChart3, ShieldCheck
} from 'lucide-react';
import { useLanguageStore } from '../../../store/useLanguageStore';

export const MappingTopBar = ({
  handleAiMatch, isAiMatching, applyGlobalRule, handleClear,
  handleIgnoreUnmapped,
  handleExportJSON, handleExportExcel, handleImportFile,
  onAddSourceFields, availableSourceCount, showAddSourceFields,
  onProfileMapping, mappedCount_forProfile = 0,
  mappedCount, ignoredCount, unmappedCount, total,
  searchText, setSearchText, statusFilter, setStatusFilter,
  filteredCount,
}) => {
  const [showTips, setShowTips] = useState(false);
  const { t } = useLanguageStore();
  const importInputRef = useRef(null);

  const isFiltered = (searchText || '').trim() !== '' || statusFilter !== 'all';

  const chips = [
    { key: 'all', label: t('wizard.mapping.filter_all') || 'Todos', count: total, dot: 'bg-slate-400' },
    { key: 'mapped', label: t('wizard.mapping.ready') || 'Listos', count: mappedCount, dot: 'bg-emerald-500' },
    { key: 'unmapped', label: t('wizard.mapping.missing') || 'Faltan', count: unmappedCount, dot: 'bg-amber-500' },
    { key: 'ignored', label: t('wizard.mapping.ignored') || 'Ignorados', count: ignoredCount, dot: 'bg-slate-400' },
  ];

  const chipStyle = (chip, isActive) => {
    if (!isActive) return 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50';
    if (chip.key === 'mapped') return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    if (chip.key === 'unmapped') return 'bg-amber-100 text-amber-700 border-amber-300';
    if (chip.key === 'ignored') return 'bg-slate-200 text-slate-700 border-slate-300';
    return 'bg-slate-200 text-slate-700 border-slate-300';
  };

  const onImportInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImportFile(file);
      e.target.value = '';
    }
  };

  return (
    <div className="mb-6">
      <div className="bg-white p-3 md:p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col gap-2.5 relative z-10">

        {/* ══ FILA 1: Botones de acción + Progress bar ══════════════ */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
            <button title={t('wizard.mapping.match_ai')} onClick={handleAiMatch} disabled={isAiMatching} className="inline-flex justify-center items-center px-3 md:px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-bold rounded-xl shadow-sm transition-all">
              {isAiMatching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              <span className="hidden md:inline ml-2">{t('wizard.mapping.match_ai') || 'Match AI'}</span>
            </button>

            <div className="relative group">
              <button title={t('wizard.mapping.global_actions')} className="inline-flex justify-center items-center px-3 md:px-4 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 text-sm font-bold rounded-xl border border-slate-200 transition-all">
                <span className="hidden md:inline mr-2">{t('wizard.mapping.global_actions') || 'Acciones Globales'}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              <div className="absolute left-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-2 bg-slate-50">{t('wizard.mapping.safe_actions') || 'Acciones Seguras'}</div>
                <button onClick={() => applyGlobalRule('TRIM_EDGES', t('wizard.mapping.trim_edges') || 'Quitar espacios a los extremos')} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition">✂️ {t('wizard.mapping.trim_edges') || 'Quitar espacios extremos'}</button>
                <div className="border-t border-slate-100 my-1"></div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-2 bg-slate-50">{t('wizard.mapping.mapping_actions') || 'Mapeo'}</div>
                <button onClick={handleIgnoreUnmapped} disabled={unmappedCount === 0} className={`w-full text-left px-4 py-2 text-sm transition ${unmappedCount === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-700 hover:bg-amber-50 hover:text-amber-700'}`}>
                  🚫 {t('wizard.mapping.ignore_unmapped') || 'Ignorar no mapeados'}{unmappedCount > 0 ? ` (${unmappedCount})` : ''}
                </button>
                {showAddSourceFields && (
                  <button
                    onClick={onAddSourceFields}
                    disabled={availableSourceCount === 0}
                    className={`w-full text-left px-4 py-2 text-sm transition ${availableSourceCount === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-700 hover:bg-indigo-50 hover:text-indigo-700'}`}
                  >
                    ➕ {t('wizard.mapping.add_source_fields') || 'Agregar campos del origen'}{availableSourceCount > 0 ? ` (${availableSourceCount})` : ''}
                  </button>
                )}
                <div className="border-t border-slate-100 my-1"></div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-2 bg-slate-50">{t('wizard.mapping.io_actions') || 'Importar / Exportar'}</div>
                <button onClick={handleExportJSON} disabled={mappedCount === 0} className={`w-full text-left px-4 py-2 text-sm transition ${mappedCount === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-700 hover:bg-blue-50 hover:text-blue-700'}`}>
                  📄 {t('wizard.mapping.export_json') || 'Exportar JSON'}
                </button>
                <button onClick={handleExportExcel} disabled={mappedCount === 0} className={`w-full text-left px-4 py-2 text-sm transition ${mappedCount === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-700 hover:bg-blue-50 hover:text-blue-700'}`}>
                  📊 {t('wizard.mapping.export_excel') || 'Exportar Excel'}
                </button>
                <button
                  onClick={() => setTimeout(() => importInputRef.current?.click(), 150)}
                  className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition"
                >
                  📥 {t('wizard.mapping.import_file') || 'Importar mapping'}
                </button>
                <a
                  href="https://docs.google.com/spreadsheets/d/1nacgUsH8rCefloNGQXbmUzGXHGCS5X0x9yuup5GA464"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition block"
                >
                  📋 {t('wizard.mapping.import_template') || 'Plantilla de importación'}
                </a>
                <div className="border-t border-slate-100 my-1"></div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-2 bg-slate-50">
                  {t('data_rules.title') || 'Calidad'}
                </div>
                <button
                  onClick={() => { onProfileMapping?.(); }}
                  disabled={mappedCount_forProfile === 0}
                  className={`w-full text-left px-4 py-2 text-sm transition flex items-center gap-2 ${mappedCount_forProfile === 0
                    ? 'text-slate-300 cursor-not-allowed'
                    : 'text-slate-700 hover:bg-indigo-50 hover:text-indigo-700'
                    }`}
                >
                  <BarChart3 className="w-4 h-4 text-indigo-500" />
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 -ml-1.5" />
                  <span>{t('wizard.mapping.analyze_suggest') || 'Analizar y sugerir reglas'}</span>
                  {mappedCount_forProfile > 0 && (
                    <span className="text-[10px] font-black text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded-full ml-auto">
                      {mappedCount_forProfile}
                    </span>
                  )}
                </button>
              </div>
            </div>

            <button title={t('wizard.mapping.clear_all')} onClick={handleClear} className="inline-flex justify-center items-center px-3 md:px-4 py-2 text-red-600 bg-red-50 hover:bg-red-100 text-sm font-bold rounded-xl transition-all">
              <Trash2 className="w-4 h-4" />
              <span className="hidden md:inline ml-2">{t('wizard.mapping.clear_all') || 'Limpiar Todo'}</span>
            </button>

            <button title={t('wizard.mapping.tips')} onClick={() => setShowTips(!showTips)} className={`inline-flex justify-center items-center p-2.5 rounded-xl transition-all border ${showTips ? 'bg-amber-100 text-amber-700 border-amber-200 shadow-inner' : 'bg-white text-amber-500 hover:bg-amber-50 hover:text-amber-600 border-slate-200 shadow-sm'}`}>
              <Lightbulb className="w-5 h-5" />
            </button>
          </div>

          {/* Progress bar — a la derecha de los botones */}
          <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden flex min-w-[40px]">
            <div className="bg-emerald-500 transition-all duration-500 h-full" style={{ width: `${total === 0 ? 0 : (mappedCount / total) * 100}%` }}></div>
            <div className="bg-amber-400 transition-all duration-500 h-full" style={{ width: `${total === 0 ? 0 : (unmappedCount / total) * 100}%` }}></div>
            <div className="bg-slate-300 transition-all duration-500 h-full" style={{ width: `${total === 0 ? 0 : (ignoredCount / total) * 100}%` }}></div>
          </div>
        </div>

        {/* ══ FILA 2: Chips + Buscador (desktop) + Limpiar ═════════ */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {/* Chips de filtro */}
          <div className="flex gap-1 overflow-x-auto no-scrollbar shrink-0">
            {chips.map(chip => {
              const isActive = statusFilter === chip.key;
              return (
                <button
                  key={chip.key}
                  onClick={() => setStatusFilter(prev => prev === chip.key ? 'all' : chip.key)}
                  className={`flex items-center gap-1 px-2 md:px-2.5 py-1 text-[11px] md:text-xs font-bold rounded-lg border whitespace-nowrap transition-all ${chipStyle(chip, isActive)}`}
                >
                  <span className={`w-2 h-2 rounded-full ${chip.dot}`}></span>
                  <span className="hidden sm:inline">{chip.label}</span>
                  <span className={`text-[10px] font-black px-1 py-0.5 rounded-full leading-none ${isActive ? 'bg-white/60' : 'bg-slate-100'}`}>
                    {chip.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Buscador — en la misma fila que chips (desktop), fila propia (mobile) */}
          {total > 8 && (
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={t('wizard.mapping.search_placeholder') || 'Buscar campo...'}
                className="w-full pl-9 pr-8 py-1.5 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition"
              />
              {searchText && (
                <button onClick={() => setSearchText('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* ══ FILA 3: Indicador + Limpiar filtros ═════════════════ */}
        {isFiltered && (
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-slate-500">
              {t('wizard.mapping.showing') || 'Mostrando'}{' '}
              <strong className="text-slate-700">{filteredCount}</strong>{' '}
              {t('wizard.mapping.of') || 'de'}{' '}
              <strong className="text-slate-700">{total}</strong>{' '}
              {t('wizard.mapping.columns') || 'columnas'}
            </span>
            <button
              onClick={() => { setSearchText(''); setStatusFilter('all'); }}
              className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-bold"
            >
              <X className="w-3.5 h-3.5" /> {t('wizard.mapping.clear_filters') || 'Limpiar filtros'}
            </button>
          </div>
        )}
      </div>

      {/* Input oculto para importar archivos */}
      <input
        ref={importInputRef}
        type="file"
        accept=".json,.xlsx,.xls"
        onChange={onImportInputChange}
        className="hidden"
      />

      {showTips && (
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 md:p-5 mt-[-10px] pt-6 shadow-sm animate-in slide-in-from-top-2 relative z-0">
          <h4 className="text-amber-800 font-bold flex items-center mb-3 text-sm">
            <Lightbulb className="w-4 h-4 mr-2" /> {t('wizard.mapping.tips_title') || 'Tips de Transformación'}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/60 p-3 rounded-xl border border-amber-100">
              <h5 className="font-bold text-slate-700 text-xs mb-1">{t('wizard.mapping.tip1_title')}</h5>
              <p className="text-[11px] text-slate-600 leading-relaxed">{t('wizard.mapping.tip1_desc')}</p>
            </div>
            <div className="bg-white/60 p-3 rounded-xl border border-amber-100">
              <h5 className="font-bold text-slate-700 text-xs mb-1">{t('wizard.mapping.tip2_title')}</h5>
              <p className="text-[11px] text-slate-600 leading-relaxed">{t('wizard.mapping.tip2_desc')}</p>
            </div>
            <div className="bg-white/60 p-3 rounded-xl border border-amber-100">
              <h5 className="font-bold text-slate-700 text-xs mb-1">{t('wizard.mapping.tip3_title')}</h5>
              <p className="text-[11px] text-slate-600 leading-relaxed">{t('wizard.mapping.tip3_desc')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
